import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Calendar, 
  Download, 
  Eye,
  Gift,
  MessageSquare,
  Plus,
  Settings,
  BarChart3,
  CreditCard,
  UserCheck,
  AlertCircle,
  UserX,
  TrendingDown,
  Target,
  ShieldAlert,
  Upload,
  Send,
  LogOut,
  Bell,
  Clock,
  CheckCircle,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import CourseManagement from './admin/CourseManagement';
import AutopayManagement from './admin/AutopayManagement';
import PaymentsTab from './admin/PaymentsTab';
import PaymentReconcileBanner from './admin/PaymentReconcileBanner';
import AITradeIdeasAccuracyTab from './admin/AITradeIdeasAccuracyTab';
import FreeVideoManagement from './admin/FreeVideoManagement';
import CouponsTab from './admin/CouponsTab';
import ServicesTab from './admin/ServicesTab';
import SettingsTab from './admin/SettingsTab';
import TradesTab from './admin/TradesTab';
import AnalyticsDashboard from './admin/AnalyticsDashboard';
import AIInsightsTab from './admin/AIInsightsTab';
import PreMarketBriefTab from './admin/PreMarketBriefTab';
import DailyQuoteTab from './admin/DailyQuoteTab';
import MarketUpdatesTab from './admin/MarketUpdatesTab';
import MarketSnapshotTab from './admin/MarketSnapshotTab';
import FyersStatusBanner from './admin/FyersStatusBanner';
import MasterclassLeadsTab from './admin/MasterclassLeadsTab';
import WorkshopSalesTab from './admin/WorkshopSalesTab';
import CourseReviewsTab from './admin/CourseReviewsTab';
import CronHealthTab from './admin/CronHealthTab';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [visitorData, setVisitorData] = useState(null);
  const [trafficSources, setTrafficSources] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [paymentAttempts, setPaymentAttempts] = useState(null);
  const [telegramLinks, setTelegramLinks] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false); // Start as false - no auth needed
  
  // Referral data state
  const [referralStats, setReferralStats] = useState(null);
  const [pointsAdjustEmail, setPointsAdjustEmail] = useState('');
  const [pointsAdjustAmount, setPointsAdjustAmount] = useState('');
  const [pointsAdjustReason, setPointsAdjustReason] = useState('');
  
  // Current logged-in admin user - loaded from localStorage
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const sessionToken = localStorage.getItem('admin_session_token');
      const adminUser = localStorage.getItem('admin_user');
      
      if (!sessionToken || !adminUser) {
        // Not logged in, redirect to login
        navigate('/admin/login');
        return;
      }
      
      try {
        // Verify session is still valid
        const response = await fetch(`${API_URL}/api/admin/auth/verify-session?session_token=${sessionToken}`);
        const data = await response.json();
        
        if (response.ok && data.valid) {
          // Session valid, set admin user
          setCurrentAdmin(JSON.parse(adminUser));
        } else {
          // Session invalid, clear storage and redirect
          localStorage.removeItem('admin_session_token');
          localStorage.removeItem('admin_email');
          localStorage.removeItem('admin_user');
          navigate('/admin/login');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // On error, still try to use cached admin data
        setCurrentAdmin(JSON.parse(adminUser));
      } finally {
        setAuthLoading(false);
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  // Logout function
  const handleLogout = async () => {
    const sessionToken = localStorage.getItem('admin_session_token');
    
    try {
      await fetch(`${API_URL}/api/admin/auth/logout?session_token=${sessionToken}`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear local storage
    localStorage.removeItem('admin_session_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_user');
    
    // Redirect to login
    navigate('/admin/login');
  };
  
  // Trade signals state
  const [trades, setTrades] = useState([]);
  const [tradeStats, setTradeStats] = useState(null);
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [newTrade, setNewTrade] = useState({
    symbol: '',
    trade_type: 'options',
    option_type: 'CE',
    strike_price: '',
    expiry_date: '',
    cmp_price: '',
    entry_price: '',
    stop_loss: '',
    targets: [{target_number: 1, price: ''}],
    visible_to: [],
    auto_expire_hours: 72
  });
  
  const [services, setServices] = useState({
    options_only: true,
    complete_package: true,
    equity_only: true,
    workshop: true,
    index_options_educational: true,
    automated_alerts_1day: true,
    automated_alerts_3day: true,
    automated_alerts_5day: true
  });

  // Payment filter state - default to 'all' to show all subscriber data
  const [paymentFilters, setPaymentFilters] = useState({
    serviceType: 'all', // all, options_only, complete_package, etc.
    status: 'all', // all, completed, failed, pending
    dateFrom: '',
    dateTo: '',
    month: ''
  });

  // Service pricing state
  const [servicePrices, setServicePrices] = useState({
    options_only: 3500,
    complete_package: 8000,
    equity_only: 4000,
    options_only_3month: 9450,
    complete_package_3month: 21600,
    equity_only_3month: 10500,
    workshop: 4999,
    index_options_educational: 2000,
    automated_alerts_1day: 200,
    automated_alerts_3day: 450,
    automated_alerts_5day: 650
  });

  // Expired users state
  const [expiredUsers, setExpiredUsers] = useState([]);

  // Check if super admin exists
  const [adminExists, setAdminExists] = useState(false);
  
  // Admin users management state
  const [adminUsers, setAdminUsers] = useState([]);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [newAdminUser, setNewAdminUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'manager',
    custom_permissions: {
      view_dashboard: true,
      view_subscribers: true,
      add_subscribers: true,
      edit_subscribers: true,
      delete_subscribers: false,
      view_payments: true,
      process_refunds: false,
      view_trades: true,
      create_trades: true,
      update_trades: true,
      delete_trades: false,
      send_performance: true,
      view_coupons: true,
      create_coupons: true,
      edit_coupons: true,
      delete_coupons: false,
      view_settings: true,
      edit_pricing: false,
      edit_services: false,
      edit_telegram_links: false,
      view_analytics: true,
      view_revenue: false,  // 👈 Can be controlled!
      manage_admin_users: false
    }
  });


  // New coupon form state
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    usage_limit: '',
    valid_until: '',
    applicable_subscription_types: ['options_only', 'complete_package', 'equity_only', 'options_only_3month', 'complete_package_3month', 'equity_only_3month', 'workshop', 'index_options_educational', 'automated_alerts_1day', 'automated_alerts_3day', 'automated_alerts_5day'],
    // Offer display fields
    show_on_website: false,
    offer_title: '',
    offer_description: '',
    creative_image_url: ''
  });
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);

  // New telegram link form state
  const [newTelegramLink, setNewTelegramLink] = useState({
    subscription_type: 'options_only',
    link: '',
    description: ''
  });


  // Helper function to check if current admin has a specific permission
  const hasPermission = (permission) => {
    if (!currentAdmin || !currentAdmin.permissions) return false;
    if (currentAdmin.role === 'super_admin') return true; // Super admin has all permissions
    return currentAdmin?.permissions?.[permission] === true;
  };
  
  // useEffect for data fetching — runs when currentAdmin is available.
  // CRITICAL FETCHES fire immediately in parallel; SECONDARY ones are deferred so the dashboard
  // becomes interactive faster after a hard refresh.
  useEffect(() => {
    if (!currentAdmin) return;

    // Critical (Dashboard tab — visible on first paint). Fire in parallel.
    Promise.allSettled([
      fetchDashboardData(),
      fetchVisitorData(),
      fetchRevenueData(),
    ]).catch(() => {});

    // Secondary tabs — defer to next tick so the first paint isn't blocked.
    // Browser will still parallelise these but UI is already responsive.
    const deferredId = setTimeout(() => {
      Promise.allSettled([
        fetchPaymentAttempts(),
        fetchTelegramLinks(),
        fetchCoupons(),
        fetchServices(),
        fetchServicePrices(),
        fetchExpiredUsers(),
        fetchTrades(),
        fetchTradeStats(),
        fetchReferralStats(),
      ]).catch(() => {});
    }, 50);

    return () => clearTimeout(deferredId);
  }, [currentAdmin]);

  const checkAdminExists = async () => {
    try {
      // Try to check if any admin users exist by calling the list endpoint
      // This will fail without auth, but we can use the setup endpoint behavior
      const response = await fetch(`${API_URL}/api/admin/setup/initial`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          setup_key: 'test',
          email: 'test@test.com',
          password: 'test',
          full_name: 'test'
        })
      });
      const data = await response.json();
      
      // If we get "Admin users already exist", set flag to true
      if (response.status === 400 && data.detail && data.detail.includes('already exist')) {
        setAdminExists(true);
      }
    } catch (error) {
      console.error('Error checking admin existence:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/stats`);
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const fetchVisitorData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/visitors?days=30`);
      const data = await response.json();
      setVisitorData(data);
      // Set traffic sources from the response
      if (data.traffic_sources) {
        setTrafficSources(data.traffic_sources);
      }
    } catch (error) {
      console.error('Failed to fetch visitor data:', error);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/revenue-analytics`);
      const data = await response.json();
      setRevenueData(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
      setLoading(false);
    }
  };

  const fetchPaymentAttempts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/payment-attempts`);
      const data = await response.json();
      setPaymentAttempts(data);
    } catch (error) {
      console.error('Failed to fetch payment attempts:', error);
    }
  };

  const fetchTelegramLinks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/telegram-links`);
      const data = await response.json();
      setTelegramLinks(data.telegram_links || []);
    } catch (error) {
      console.error('Failed to fetch telegram links:', error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons`);
      const data = await response.json();
      setCoupons(data.coupons || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  };

  const fetchReferralStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referrals`);
      const data = await response.json();
      setReferralStats(data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    }
  };

  const handleAdjustPoints = async (e) => {
    e.preventDefault();
    if (!pointsAdjustEmail || !pointsAdjustAmount || !pointsAdjustReason) {
      alert('Please fill all fields');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/referrals/adjust-points`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: pointsAdjustEmail,
          points: parseFloat(pointsAdjustAmount),
          reason: pointsAdjustReason
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`Points adjusted! New balance: ${data.new_balance}`);
        setPointsAdjustEmail('');
        setPointsAdjustAmount('');
        setPointsAdjustReason('');
        fetchReferralStats();
      } else {
        alert(data.detail || 'Failed to adjust points');
      }
    } catch (error) {
      console.error('Failed to adjust points:', error);
      alert('Failed to adjust points');
    }
  };

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/services`);
      const data = await response.json();
      setServices(data.services || {
        options_only: true,
        complete_package: true,
        equity_only: true,
        workshop: true,
        index_options_educational: true,
        automated_alerts_1day: true,
        automated_alerts_3day: true,
        automated_alerts_5day: true
      });
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };


  const fetchTrades = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/trade-signals/all?status=active&limit=50`);
      const data = await response.json();
      setTrades(data.trades || []);
    } catch (error) {
      console.error('Failed to fetch trades:', error);
    }
  };

  const fetchTradeStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/trade-signals/stats`);
      const data = await response.json();
      setTradeStats(data);
    } catch (error) {
      console.error('Failed to fetch trade stats:', error);
    }
  };


  const toggleService = async (serviceType) => {
    try {
      const newStatus = !services[serviceType];
      const response = await fetch(`${API_URL}/api/admin/services/${serviceType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newStatus })
      });

      if (response.ok) {
        setServices(prev => ({
          ...prev,
          [serviceType]: newStatus
        }));
        alert(`Service ${serviceType} ${newStatus ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (error) {
      console.error('Failed to toggle service:', error);
      alert('Failed to toggle service');
    }
  };

  const fetchServicePrices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/prices?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      const data = await response.json();
      if (data.prices) {
        setServicePrices(data.prices);
      }
    } catch (error) {
      console.error('Failed to fetch service prices:', error);
    }
  };

  const fetchExpiredUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/expired-users`);
      const data = await response.json();
      if (data.expired_users) {
        setExpiredUsers(data.expired_users);
      }
    } catch (error) {
      console.error('Failed to fetch expired users:', error);
    }
  };

  const updateServicePrice = async (serviceType, newPrice) => {
    // Client-side validation — backend rejects null/0/negative prices with HTTP 400
    const numericPrice = Number(newPrice);
    if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
      alert('Please enter a valid price greater than 0 before clicking Update.');
      return;
    }

    // Hard timeout so the user never sees an indefinite spinner if the network hangs
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(`${API_URL}/api/admin/prices/${serviceType}?_t=${Date.now()}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
        cache: 'no-store',
        body: JSON.stringify({ price: numericPrice }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setServicePrices(prev => ({
          ...prev,
          [serviceType]: numericPrice
        }));
        alert(`Price for ${serviceType} updated successfully to ₹${numericPrice.toLocaleString()}`);
        fetchServicePrices();
      } else {
        // Surface backend's actual error so admins can act on it
        alert(`Failed to update price (HTTP ${response.status}): ${data.detail || response.statusText || 'Unknown error'}`);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Failed to update service price:', error);
      // "Failed to fetch" usually means a browser extension / service worker / network blocked the request
      if (error.name === 'AbortError') {
        alert('Update timed out after 15s. Check your internet connection and try again.');
      } else if (error.message === 'Failed to fetch') {
        alert('Network blocked the request. This is usually caused by:\n• An ad-blocker or browser extension blocking /api/admin/* paths\n• A stale service worker (try a hard refresh: Ctrl+Shift+R)\n• A VPN or corporate firewall\n\nTry incognito mode or disable extensions, then retry.');
      } else {
        alert(`Failed to update service price: ${error.message || 'Network error'}`);
      }
    }
  };

  // Filter payments based on criteria
  const getFilteredPayments = () => {
    if (!paymentAttempts?.payment_attempts) return [];
    
    let filtered = [...paymentAttempts.payment_attempts];
    
    // Filter by service type
    if (paymentFilters.serviceType && paymentFilters.serviceType !== 'all') {
      filtered = filtered.filter(p => p.service_type === paymentFilters.serviceType);
    }
    
    // Filter by status
    if (paymentFilters.status !== 'all') {
      filtered = filtered.filter(p => p.status === paymentFilters.status);
    }
    
    // Filter by date range
    if (paymentFilters.dateFrom) {
      const fromDate = new Date(paymentFilters.dateFrom);
      filtered = filtered.filter(p => {
        const paymentDate = new Date(p.attempted_at);
        return paymentDate >= fromDate;
      });
    }
    
    if (paymentFilters.dateTo) {
      const toDate = new Date(paymentFilters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(p => {
        const paymentDate = new Date(p.attempted_at);
        return paymentDate <= toDate;
      });
    }
    
    // Filter by month
    if (paymentFilters.month) {
      const [year, month] = paymentFilters.month.split('-');
      filtered = filtered.filter(p => {
        const paymentDate = new Date(p.attempted_at);
        return paymentDate.getFullYear() === parseInt(year) && 
               (paymentDate.getMonth() + 1) === parseInt(month);
      });
    }
    
    return filtered;
  };

  // Handle image upload for coupon creative
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size: 5MB');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/admin/upload-image`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewCoupon(prev => ({ ...prev, creative_image_url: `${API_URL}${data.image_url}` }));
        alert('Image uploaded successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to upload image: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    
    // Validate that at least one service is selected
    if (!newCoupon.applicable_subscription_types || newCoupon.applicable_subscription_types.length === 0) {
      alert('Please select at least one applicable service');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newCoupon,
          discount_value: parseFloat(newCoupon.discount_value),
          min_order_amount: parseFloat(newCoupon.min_order_amount || 0),
          usage_limit: newCoupon.usage_limit ? parseInt(newCoupon.usage_limit) : null,
          valid_until: newCoupon.valid_until ? new Date(newCoupon.valid_until).toISOString() : null,
          applicable_subscription_types: newCoupon.applicable_subscription_types,
          show_on_website: newCoupon.show_on_website,
          offer_title: newCoupon.offer_title || null,
          offer_description: newCoupon.offer_description || null,
          creative_image_url: newCoupon.creative_image_url || null
        })
      });

      if (response.ok) {
        alert('Coupon created successfully!');
        setNewCoupon({
          code: '',
          discount_type: 'percentage',
          discount_value: '',
          min_order_amount: '',
          usage_limit: '',
          valid_until: '',
          applicable_subscription_types: ['options_only', 'complete_package', 'equity_only', 'options_only_3month', 'complete_package_3month', 'equity_only_3month', 'workshop', 'index_options_educational', 'automated_alerts_1day', 'automated_alerts_3day', 'automated_alerts_5day'],
          show_on_website: false,
          offer_title: '',
          offer_description: '',
          creative_image_url: ''
        });
        fetchCoupons();
      }
    } catch (error) {
      console.error('Failed to create coupon:', error);
      alert('Failed to create coupon');
    }
  };

  // Edit coupon state
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editCouponData, setEditCouponData] = useState(null);

  const handleToggleCouponStatus = async (couponCode, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons/${couponCode}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchCoupons();
      } else {
        alert('Failed to update coupon status');
      }
    } catch (error) {
      console.error('Failed to toggle coupon:', error);
      alert('Failed to update coupon status');
    }
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon.code);
    setEditCouponData({
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value || '',
      usage_limit: coupon.usage_limit || '',
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
      applicable_subscription_types: coupon.applicable_subscription_types || [],
      show_on_website: coupon.show_on_website || false,
      offer_title: coupon.offer_title || '',
      offer_description: coupon.offer_description || ''
    });
  };

  const handleSaveEditCoupon = async () => {
    try {
      const updatePayload = {
        ...editCouponData,
        discount_value: parseFloat(editCouponData.discount_value),
        usage_limit: editCouponData.usage_limit ? parseInt(editCouponData.usage_limit) : null,
        valid_until: editCouponData.valid_until ? new Date(editCouponData.valid_until).toISOString() : null
      };

      const response = await fetch(`${API_URL}/api/admin/coupons/${editingCoupon}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        alert('Coupon updated successfully!');
        setEditingCoupon(null);
        setEditCouponData(null);
        fetchCoupons();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update coupon');
      }
    } catch (error) {
      console.error('Failed to update coupon:', error);
      alert('Failed to update coupon');
    }
  };

  const handleCancelEditCoupon = () => {
    setEditingCoupon(null);
    setEditCouponData(null);
  };

  const handleDeleteCoupon = async (couponCode) => {
    if (!confirm(`Are you sure you want to delete coupon "${couponCode}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons/${couponCode}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Coupon deleted successfully!');
        fetchCoupons();
      } else {
        alert('Failed to delete coupon');
      }
    } catch (error) {
      console.error('Failed to delete coupon:', error);
      alert('Failed to delete coupon');
    }
  };

  const handleUpdateTelegramLink = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/admin/telegram-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTelegramLink)
      });

      if (response.ok) {
        alert('Telegram link updated successfully!');
        setNewTelegramLink({
          subscription_type: 'options_only',
          link: '',
          description: ''
        });
        fetchTelegramLinks();
      }
    } catch (error) {
      console.error('Failed to update telegram link:', error);
      alert('Failed to update telegram link');
    }
  };

  const handleRemoveExpiredUsers = async () => {
    if (!confirm('Are you sure you want to remove all expired users from Telegram groups? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/remove-expired-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      
      if (response.ok) {
        alert(`Successfully processed expired users removal. ${data.message || 'Operation completed.'}`);
        // Refresh expired users list
        fetchExpiredUsers();
      } else {
        alert(`Failed to remove expired users: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to remove expired users:', error);
      alert('Failed to remove expired users. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (type) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/export/${type}`);
      const data = await response.json();
      
      // Convert to CSV and download
      const csv = convertToCSV(data.data, type);
      downloadCSV(csv, `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error('Failed to export data:', error);
      alert('Failed to export data');
    }
  };

  const convertToCSV = (data, type) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => 
      Object.values(item).map(value => 
        typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      ).join(',')
    ).join('\n');
    
    return `${headers}\n${rows}`;
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - this shouldn't normally show as we redirect in useEffect
  if (!currentAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* VERSION MARKER - FULL FEATURED ADMIN DASHBOARD v2.0 */}
      <div style={{display: 'none'}} data-version="full-admin-v2.0-dec-2024"></div>
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Upper Circuit Stocks - Admin Dashboard</h1>
              {currentAdmin && (
                <p className="text-sm text-gray-600 mt-1">
                  Logged in as: <span className="font-semibold">{currentAdmin.email}</span> 
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{currentAdmin.role}</span>
                </p>
              )}
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
          <div className="flex items-center space-x-4 pb-4 overflow-x-auto">
            <div className="flex space-x-2">
              {hasPermission('view_dashboard') && (
                <Button 
                  variant={activeTab === 'dashboard' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('dashboard')}
                  size="sm"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              )}
              {hasPermission('view_payments') && (
                <Button 
                  variant={activeTab === 'payments' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('payments')}
                  size="sm"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payments
                </Button>
              )}
              {hasPermission('view_subscribers') && (
                <Button 
                  variant={activeTab === 'subscribers' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('subscribers')}
                  size="sm"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Subscribers
                </Button>
              )}
              {hasPermission('view_trades') && (
                <Button 
                  variant={activeTab === 'trades' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('trades')}
                  size="sm"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Trades
                </Button>
              )}
              {hasPermission('view_coupons') && (
                <Button 
                  variant={activeTab === 'coupons' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('coupons')}
                  size="sm"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Coupons
                </Button>
              )}
              {hasPermission('view_settings') && (
                <Button 
                  variant={activeTab === 'services' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('services')}
                  size="sm"
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Services
                </Button>
              )}
              {hasPermission('view_settings') && (
                <Button 
                  variant={activeTab === 'settings' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('settings')}
                  size="sm"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              )}
              {hasPermission('view_settings') && (
                <Button 
                  variant={activeTab === 'referrals' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('referrals')}
                  size="sm"
                >
                  🎁 Referrals
                </Button>
              )}
              {hasPermission('view_subscribers') && (
                <Button 
                  variant={activeTab === 'alerts' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('alerts')}
                  size="sm"
                  className={activeTab !== 'alerts' ? 'border-red-300 text-red-600 hover:bg-red-50' : ''}
                >
                  🔔 Expiry Alerts
                </Button>
              )}
              {hasPermission('view_settings') && (
                <Button 
                  variant={activeTab === 'reviews' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('reviews')}
                  size="sm"
                >
                  Reviews
                </Button>
              )}
              <Button 
                variant={activeTab === 'courses' ? 'default' : 'outline'}
                onClick={() => setActiveTab('courses')}
                size="sm"
                data-testid="admin-courses-tab"
              >
                Courses
              </Button>
              <Button 
                variant={activeTab === 'autopay' ? 'default' : 'outline'}
                onClick={() => setActiveTab('autopay')}
                size="sm"
                data-testid="admin-autopay-tab"
              >
                Autopay
              </Button>
              <Button 
                variant={activeTab === 'free_videos' ? 'default' : 'outline'}
                onClick={() => setActiveTab('free_videos')}
                size="sm"
                data-testid="admin-free-videos-tab"
              >
                Free Videos
              </Button>
              <Button
                variant={activeTab === 'ai_insights' ? 'default' : 'outline'}
                onClick={() => setActiveTab('ai_insights')}
                size="sm"
                data-testid="admin-ai-insights-tab"
                className={activeTab === 'ai_insights' ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}
              >
                ✨ AI Insights
              </Button>
              <Button
                variant={activeTab === 'premarket_brief' ? 'default' : 'outline'}
                onClick={() => setActiveTab('premarket_brief')}
                size="sm"
                data-testid="admin-premarket-brief-tab"
                className={activeTab === 'premarket_brief' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
              >
                📈 Pre-Market Brief
              </Button>
              <Button
                variant={activeTab === 'daily_quote' ? 'default' : 'outline'}
                onClick={() => setActiveTab('daily_quote')}
                size="sm"
                data-testid="admin-daily-quote-tab"
                className={activeTab === 'daily_quote' ? 'bg-yellow-600 hover:bg-yellow-700 text-white' : 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'}
              >
                📜 Daily Quote
              </Button>
              <Button
                variant={activeTab === 'market_updates' ? 'default' : 'outline'}
                onClick={() => setActiveTab('market_updates')}
                size="sm"
                data-testid="admin-market-updates-tab"
                className={activeTab === 'market_updates' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
              >
                📢 Market Updates
              </Button>
              <Button
                variant={activeTab === 'market_snapshot' ? 'default' : 'outline'}
                onClick={() => setActiveTab('market_snapshot')}
                size="sm"
                data-testid="admin-market-snapshot-tab"
                className={activeTab === 'market_snapshot' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
              >
                📊 Daily Snapshot
              </Button>
              <Button
                variant={activeTab === 'ai_ideas_accuracy' ? 'default' : 'outline'}
                onClick={() => setActiveTab('ai_ideas_accuracy')}
                size="sm"
                data-testid="admin-ai-ideas-accuracy-tab"
                className={activeTab === 'ai_ideas_accuracy' ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}
              >
                🎯 AI Ideas Accuracy
              </Button>
              <Button
                variant={activeTab === 'course_reviews' ? 'default' : 'outline'}
                onClick={() => setActiveTab('course_reviews')}
                size="sm"
                data-testid="admin-course-reviews-tab"
                className={activeTab === 'course_reviews' ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'border-rose-300 text-rose-700 hover:bg-rose-50'}
              >
                ⭐ Course Reviews
              </Button>
              <Button
                variant={activeTab === 'cron_health' ? 'default' : 'outline'}
                onClick={() => setActiveTab('cron_health')}
                size="sm"
                data-testid="admin-cron-health-tab"
                className={activeTab === 'cron_health' ? 'bg-sky-600 hover:bg-sky-700 text-white' : 'border-sky-300 text-sky-700 hover:bg-sky-50'}
              >
                🔄 Cron Health
              </Button>
              <Button
                variant={activeTab === 'masterclass' ? 'default' : 'outline'}
                onClick={() => setActiveTab('masterclass')}
                size="sm"
                data-testid="admin-masterclass-tab"
                className={activeTab === 'masterclass' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}
              >
                🎓 Masterclass Leads
              </Button>
              <Button
                variant={activeTab === 'workshop' ? 'default' : 'outline'}
                onClick={() => setActiveTab('workshop')}
                size="sm"
                data-testid="admin-workshop-tab"
                className={activeTab === 'workshop' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
              >
                🎯 Workshop Sales
              </Button>
              </div>
            </div>
          </div>
        </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <FyersStatusBanner onGoToSettings={() => setActiveTab('settings')} />
        {activeTab === 'dashboard' && (
          <AnalyticsDashboard
            revenueData={revenueData}
            visitorData={visitorData}
            trafficSources={trafficSources}
            dashboardData={dashboardData}
            hasPermission={hasPermission}
            exportData={exportData}
          />
        )}

        {activeTab === 'masterclass' && (
          <MasterclassLeadsTab API_URL={API_URL} />
        )}

        {activeTab === 'workshop' && (
          <WorkshopSalesTab API_URL={API_URL} />
        )}

        {activeTab === 'payments' && (
          <>
            <PaymentReconcileBanner API_URL={API_URL} onDone={fetchPaymentAttempts} />
            <PaymentsTab
              paymentAttempts={paymentAttempts}
              paymentFilters={paymentFilters}
              setPaymentFilters={setPaymentFilters}
              getFilteredPayments={getFilteredPayments}
            />
          </>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-6">
            {/* Create Subscriber Form */}
            <Card className="bg-white border-green-200">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  Create New Subscriber (Login ID)
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Manually create a subscriber with login credentials. They can login using their phone number.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form 
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const data = {
                      name: formData.get('name'),
                      phone: formData.get('phone'),
                      email: formData.get('email'),
                      telegram_id: formData.get('telegram_id') || '',
                      initial_points: parseFloat(formData.get('initial_points')) || 0
                    };
                    
                    try {
                      const response = await fetch(`${API_URL}/api/admin/subscribers/create-manual`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(data)
                      });
                      const result = await response.json();
                      
                      if (response.ok && result.status === 'created') {
                        alert(`✅ Subscriber Created!\n\nName: ${result.user_name}\nLogin ID (Phone): ${result.login_id}\nReferral Code: ${result.referral_code}\nEmail: ${result.user_email}\nInitial Points: ${result.initial_points}`);
                        e.target.reset();
                        fetchPaymentAttempts();
                        fetchReferralStats();
                      } else if (result.status === 'exists') {
                        alert(`⚠️ ${result.message}\n\nExisting Code: ${result.referral_code}\nName: ${result.user_name}`);
                      } else {
                        alert(`Error: ${result.detail || 'Failed to create subscriber'}`);
                      }
                    } catch (error) {
                      alert('Error creating subscriber');
                    }
                  }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  <div>
                    <Label className="text-gray-700">Name *</Label>
                    <Input name="name" placeholder="Full Name" required className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Mobile Number * (Login ID)</Label>
                    <Input name="phone" placeholder="9876543210" required className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Email *</Label>
                    <Input name="email" type="email" placeholder="email@example.com" required className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Telegram ID</Label>
                    <Input name="telegram_id" placeholder="@username (optional)" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Initial Points</Label>
                    <Input name="initial_points" type="number" placeholder="0" defaultValue="0" className="mt-1" />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Subscriber
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Subscribers Table */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  All Subscribers
                </CardTitle>
                <CardDescription className="text-gray-600">
                  View all subscribers with their login IDs (phone numbers), referral codes, and subscription details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-3 px-3 font-medium text-gray-700">Subscriber</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-700">Login ID (Phone)</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-700">Email</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-700">Referral Code</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-700">Package</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-700">Status</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-700">Expiry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentAttempts?.payment_attempts
                        ?.filter(a => a.status === 'completed')
                        .map((sub, index) => {
                          const endDate = sub.subscription_end_date ? new Date(sub.subscription_end_date) : null;
                          const isExpired = endDate && endDate < new Date();
                          const isExpiringSoon = endDate && !isExpired && (endDate - new Date()) / (1000 * 60 * 60 * 24) <= 7;
                          
                          return (
                            <tr key={sub.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-3">
                                <div className="font-medium text-gray-900">{sub.customer_name}</div>
                                <div className="text-xs text-gray-500">{sub.telegram_id || 'No Telegram'}</div>
                              </td>
                              <td className="py-3 px-3">
                                <code className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-sm font-mono">
                                  {sub.customer_phone || 'N/A'}
                                </code>
                              </td>
                              <td className="py-3 px-3 text-gray-600 text-xs">
                                {sub.customer_email}
                              </td>
                              <td className="py-3 px-3">
                                {sub.subscriber_referral_code ? (
                                  <code className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-sm font-mono">
                                    {sub.subscriber_referral_code}
                                  </code>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs border-purple-300 text-purple-600 hover:bg-purple-50"
                                    onClick={async () => {
                                      try {
                                        const response = await fetch(`${API_URL}/api/admin/referrals/generate-code/${encodeURIComponent(sub.customer_phone)}`, {
                                          method: 'POST'
                                        });
                                        const result = await response.json();
                                        if (response.ok) {
                                          alert(`✅ Referral Code Generated!\n\nCode: ${result.referral_code}\nPoints Awarded: ${result.points_awarded?.toFixed(0) || 0}`);
                                          fetchPaymentAttempts();
                                        } else {
                                          alert(`Error: ${result.detail || 'Failed to generate code'}`);
                                        }
                                      } catch (error) {
                                        alert('Error generating code');
                                      }
                                    }}
                                  >
                                    Generate Code
                                  </Button>
                                )}
                              </td>
                              <td className="py-3 px-3">
                                <Badge variant="outline" className="border-golden text-golden">
                                  {sub.service_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </Badge>
                              </td>
                              <td className="py-3 px-3">
                                {isExpired ? (
                                  <Badge className="bg-red-100 text-red-700">Expired</Badge>
                                ) : isExpiringSoon ? (
                                  <Badge className="bg-yellow-100 text-yellow-700">Expiring Soon</Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-700">Active</Badge>
                                )}
                              </td>
                              <td className="py-3 px-3 text-gray-600 text-xs">
                                {sub.subscription_end_date 
                                  ? new Date(sub.subscription_end_date).toLocaleDateString()
                                  : 'N/A'
                                }
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                
                {/* Bulk Actions & Summary */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-4 mb-4">
                    <Button
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={async () => {
                        if (!confirm('Generate referral codes for ALL subscribers without codes?')) return;
                        try {
                          const response = await fetch(`${API_URL}/api/admin/referrals/backfill`, {
                            method: 'POST'
                          });
                          const result = await response.json();
                          if (response.ok) {
                            alert(`✅ Codes Generated!\n\nCreated: ${result.created_profiles}\nUpdated: ${result.updated_profiles}\nProcessed: ${result.total_subscriptions_processed}`);
                            fetchPaymentAttempts();
                          } else {
                            alert('Failed to generate codes');
                          }
                        } catch (error) {
                          alert('Error generating codes');
                        }
                      }}
                    >
                      🎁 Generate All Missing Codes
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={async () => {
                        if (!confirm('Award 5% points to ALL paid subscribers (excluding Telegram Alerts)?')) return;
                        try {
                          const response = await fetch(`${API_URL}/api/admin/referrals/backfill-points`, {
                            method: 'POST'
                          });
                          const result = await response.json();
                          if (response.ok) {
                            alert(`✅ Points Awarded!\n\nSubscribers: ${result.subscribers_awarded}\nTotal Points: ${result.total_points_awarded?.toFixed(0)}\nAlerts Skipped: ${result.telegram_alerts_skipped}`);
                            fetchPaymentAttempts();
                            fetchReferralStats();
                          } else {
                            alert('Failed to award points');
                          }
                        } catch (error) {
                          alert('Error awarding points');
                        }
                      }}
                    >
                      💰 Award 5% Points to All
                    </Button>
                  </div>
                  <div className="flex gap-6 text-sm">
                    <div>
                      <span className="text-gray-500">Total Subscribers:</span>
                      <span className="ml-2 font-semibold text-gray-900">
                        {paymentAttempts?.payment_attempts?.filter(a => a.status === 'completed').length || 0}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">With Referral Codes:</span>
                      <span className="ml-2 font-semibold text-purple-600">
                        {paymentAttempts?.payment_attempts?.filter(a => a.status === 'completed' && a.subscriber_referral_code).length || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Trades Tab */}
        {activeTab === 'trades' && (
          <TradesTab
            trades={trades}
            tradeStats={tradeStats}
            showTradeForm={showTradeForm}
            setShowTradeForm={setShowTradeForm}
            newTrade={newTrade}
            setNewTrade={setNewTrade}
            hasPermission={hasPermission}
            fetchTrades={fetchTrades}
            API_URL={API_URL}
          />
        )}

        {activeTab === 'coupons' && (
          <CouponsTab
            coupons={coupons}
            newCoupon={newCoupon}
            setNewCoupon={setNewCoupon}
            handleCreateCoupon={handleCreateCoupon}
            handleDeleteCoupon={handleDeleteCoupon}
            hasPermission={hasPermission}
          />
        )}

        {activeTab === 'services' && (
          <ServicesTab
            services={services}
            servicePrices={servicePrices}
            setServicePrices={setServicePrices}
            toggleService={toggleService}
            updateServicePrice={updateServicePrice}
            expiredUsers={expiredUsers}
            handleRemoveExpiredUsers={handleRemoveExpiredUsers}
            hasPermission={hasPermission}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            telegramLinks={telegramLinks}
            newTelegramLink={newTelegramLink}
            setNewTelegramLink={setNewTelegramLink}
            handleUpdateTelegramLink={handleUpdateTelegramLink}
            adminUsers={adminUsers}
            setAdminUsers={setAdminUsers}
            newAdminUser={newAdminUser}
            setNewAdminUser={setNewAdminUser}
            showCreateUserForm={showCreateUserForm}
            setShowCreateUserForm={setShowCreateUserForm}
            hasPermission={hasPermission}
            currentAdmin={currentAdmin}
            adminExists={adminExists}
            expiredUsers={expiredUsers}
            handleRemoveExpiredUsers={handleRemoveExpiredUsers}
            API_URL={API_URL}
          />
        )}

        {/* REFERRALS TAB */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            {/* Referral Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-4">
                  <p className="text-sm text-purple-100">Total Points Issued</p>
                  <p className="text-2xl font-bold">{referralStats?.summary?.total_points_issued?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-4">
                  <p className="text-sm text-green-100">Points Redeemed</p>
                  <p className="text-2xl font-bold">{referralStats?.summary?.total_points_redeemed?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-4">
                  <p className="text-sm text-blue-100">Active Points</p>
                  <p className="text-2xl font-bold">{referralStats?.summary?.total_active_points?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="p-4">
                  <p className="text-sm text-orange-100">Total Referrals</p>
                  <p className="text-2xl font-bold">{referralStats?.summary?.total_referrals || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Generate Referral Codes for Existing Subscribers */}
            <Card className="bg-white border-blue-200">
              <CardHeader>
                <CardTitle className="text-gray-900">🎁 Generate Referral Codes</CardTitle>
                <CardDescription className="text-gray-600">
                  Create referral codes for existing subscribers who don't have one yet
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <Button 
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API_URL}/api/admin/referrals/backfill`, {
                          method: 'POST'
                        });
                        const result = await response.json();
                        if (response.ok) {
                          alert(`✅ Backfill Complete!\n\nCreated: ${result.created_profiles} new profiles\nUpdated: ${result.updated_profiles} profiles\nTotal processed: ${result.total_subscriptions_processed}`);
                          fetchReferralStats();
                        } else {
                          alert('Failed to generate referral codes');
                        }
                      } catch (error) {
                        console.error('Backfill error:', error);
                        alert('Error generating referral codes');
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Generate Missing Referral Codes
                  </Button>
                  <p className="text-sm text-gray-500">
                    This will create referral codes for all paid subscribers who don't have one
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Manual Points Adjustment */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900">🔧 Manual Points Adjustment</CardTitle>
                <CardDescription className="text-gray-600">Add or deduct points from a user's balance</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAdjustPoints} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-gray-700">User Email</Label>
                    <Input
                      type="email"
                      placeholder="user@example.com"
                      value={pointsAdjustEmail}
                      onChange={(e) => setPointsAdjustEmail(e.target.value)}
                      className="bg-white border-gray-300 text-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Points (+ or -)</Label>
                    <Input
                      type="number"
                      placeholder="100 or -50"
                      value={pointsAdjustAmount}
                      onChange={(e) => setPointsAdjustAmount(e.target.value)}
                      className="bg-white border-gray-300 text-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Reason</Label>
                    <Input
                      placeholder="Manual bonus, correction, etc."
                      value={pointsAdjustReason}
                      onChange={(e) => setPointsAdjustReason(e.target.value)}
                      className="bg-white border-gray-300 text-gray-900"
                      required
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700 w-full">
                      Adjust Points
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Top Referrers */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900">🏆 Top Referrers</CardTitle>
                <CardDescription className="text-gray-600">Users with most referral earnings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-700">User</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Referral Code</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Referrals</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Points Earned</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Points Balance</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Points Redeemed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(referralStats?.top_referrers || []).map((user, index) => (
                        <tr key={user.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3">
                            <div>
                              <p className="font-medium text-gray-900">{user.user_name}</p>
                              <p className="text-xs text-gray-500">{user.user_email}</p>
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-mono">
                              {user.referral_code}
                            </code>
                          </td>
                          <td className="py-2 px-3 text-gray-600">
                            {user.successful_referrals || 0}
                          </td>
                          <td className="py-2 px-3 text-green-600 font-medium">
                            {(user.total_points_earned || 0).toLocaleString()}
                          </td>
                          <td className="py-2 px-3 text-purple-600 font-medium">
                            {(user.points_balance || 0).toLocaleString()}
                          </td>
                          <td className="py-2 px-3 text-gray-600">
                            {(user.total_points_redeemed || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      {(!referralStats?.top_referrers || referralStats.top_referrers.length === 0) && (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-gray-500">
                            No referral data yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Recent Referral Transactions */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900">📊 Recent Referral Activity</CardTitle>
                <CardDescription className="text-gray-600">Latest successful referrals</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Date</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Referrer</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Referee</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Subscription</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Points Awarded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(referralStats?.recent_referrals || []).map((ref, index) => (
                        <tr key={ref.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 text-gray-600">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-2 px-3 text-gray-900">
                            {ref.referrer_email}
                          </td>
                          <td className="py-2 px-3">
                            <div>
                              <p className="text-gray-900">{ref.referee_name}</p>
                              <p className="text-xs text-gray-500">{ref.referee_email}</p>
                            </div>
                          </td>
                          <td className="py-2 px-3 text-gray-600">
                            ₹{ref.subscription_amount?.toLocaleString()}
                          </td>
                          <td className="py-2 px-3 text-green-600 font-medium">
                            +{ref.points_awarded?.toLocaleString()} pts
                          </td>
                        </tr>
                      ))}
                      {(!referralStats?.recent_referrals || referralStats.recent_referrals.length === 0) && (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-gray-500">
                            No referral transactions yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Expiry Alerts Tab */}
        {activeTab === 'alerts' && (
          <ExpiryAlertsTab API_URL={API_URL} />
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <ReviewsTab API_URL={API_URL} />
        )}
        {activeTab === 'courses' && (
          <CourseManagement sessionToken={localStorage.getItem('admin_session_token')} />
        )}
        {activeTab === 'autopay' && (
          <AutopayManagement sessionToken={localStorage.getItem('admin_session_token')} />
        )}
        {activeTab === 'free_videos' && (
          <FreeVideoManagement sessionToken={localStorage.getItem('admin_session_token')} />
        )}
        {activeTab === 'ai_insights' && (
          <AIInsightsTab />
        )}
        {activeTab === 'premarket_brief' && (
          <PreMarketBriefTab />
        )}
        {activeTab === 'daily_quote' && (
          <DailyQuoteTab />
        )}
        {activeTab === 'market_updates' && (
          <MarketUpdatesTab />
        )}
        {activeTab === 'market_snapshot' && (
          <MarketSnapshotTab />
        )}
        {activeTab === 'ai_ideas_accuracy' && (
          <AITradeIdeasAccuracyTab />
        )}
        {activeTab === 'course_reviews' && (
          <CourseReviewsTab />
        )}
        {activeTab === 'cron_health' && (
          <CronHealthTab />
        )}
      </div>
    </div>
  );
};

// Reviews Management Tab Component
const ReviewsTab = ({ API_URL }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '', rating: 5 });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/reviews`);
      const data = await response.json();
      if (response.ok) {
        setReviews(data.reviews || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}/approve`, {
        method: 'POST'
      });
      if (response.ok) {
        fetchReviews();
      }
    } catch (error) {
      alert('Failed to approve review');
    }
  };

  const handleReject = async (reviewId) => {
    if (!confirm('Reject this review?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}/reject`, {
        method: 'POST'
      });
      if (response.ok) {
        fetchReviews();
      }
    } catch (error) {
      alert('Failed to reject review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!confirm('Delete this review permanently?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchReviews();
      }
    } catch (error) {
      alert('Failed to delete review');
    }
  };

  const handleEdit = (review) => {
    setEditingReview(review.id);
    setEditForm({
      title: review.title,
      content: review.content,
      rating: review.rating
    });
  };

  const handleSaveEdit = async (reviewId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        setEditingReview(null);
        fetchReviews();
      }
    } catch (error) {
      alert('Failed to update review');
    }
  };

  const handleToggleFeatured = async (reviewId, currentFeatured) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_featured: !currentFeatured })
      });
      if (response.ok) {
        fetchReviews();
      }
    } catch (error) {
      alert('Failed to update review');
    }
  };

  const filteredReviews = filter === 'all' 
    ? reviews 
    : reviews.filter(r => r.status === filter);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={i < rating ? 'text-yellow-500' : 'text-gray-300'}>★</span>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading reviews...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
            <p className="text-sm text-gray-500">Total Reviews</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</p>
            <p className="text-sm text-yellow-700">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.approved || 0}</p>
            <p className="text-sm text-green-700">Approved</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.rejected || 0}</p>
            <p className="text-sm text-red-700">Rejected</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? '' : 'hover:bg-gray-100'}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'all' && ` (${stats[f] || 0})`}
          </Button>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={fetchReviews}
          className="ml-auto"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.length === 0 ? (
          <Card className="bg-gray-50">
            <CardContent className="p-8 text-center text-gray-500">
              No reviews found
            </CardContent>
          </Card>
        ) : (
          filteredReviews.map((review) => (
            <Card key={review.id} className="bg-white">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  {/* Review Content */}
                  <div className="flex-1">
                    {editingReview === review.id ? (
                      <div className="space-y-3">
                        <Input
                          value={editForm.title}
                          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          placeholder="Title"
                        />
                        <textarea
                          value={editForm.content}
                          onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                          className="w-full p-2 border rounded min-h-[80px]"
                          placeholder="Review content"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleSaveEdit(review.id)}>
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingReview(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{renderStars(review.rating)}</span>
                          <Badge 
                            className={
                              review.status === 'approved' ? 'bg-green-100 text-green-700' :
                              review.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }
                          >
                            {review.status}
                          </Badge>
                          {review.is_featured && (
                            <Badge className="bg-purple-100 text-purple-700">Featured</Badge>
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900">{review.title}</h4>
                        <p className="text-gray-600 text-sm mt-1">{review.content}</p>
                        <div className="mt-2 text-xs text-gray-500">
                          <span className="font-medium">{review.user_name}</span>
                          {review.user_email && <span className="ml-2">({review.user_email})</span>}
                          <span className="ml-2">• {new Date(review.created_at).toLocaleDateString()}</span>
                          {review.subscription_type && (
                            <span className="ml-2">• {review.subscription_type}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  {editingReview !== review.id && (
                    <div className="flex flex-wrap gap-2 md:flex-col">
                      {review.status === 'pending' && (
                        <>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleApprove(review.id)}
                          >
                            ✓ Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleReject(review.id)}
                          >
                            ✗ Reject
                          </Button>
                        </>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEdit(review)}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleToggleFeatured(review.id, review.is_featured)}
                      >
                        {review.is_featured ? '★ Unfeature' : '☆ Feature'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(review.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Expiry Alerts Tab Component
const ExpiryAlertsTab = ({ API_URL }) => {
  const [alertsData, setAlertsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/subscription-alerts`);
      const data = await response.json();
      if (response.ok) {
        setAlertsData(data);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendAlerts = async () => {
    if (!confirm('Send expiry alerts to admin email and Telegram?')) return;
    
    try {
      setSending(true);
      const response = await fetch(`${API_URL}/api/admin/subscription-alerts/send`, {
        method: 'POST'
      });
      const result = await response.json();
      if (response.ok) {
        alert(`✅ Alerts Sent!\n\nExpired: ${result.expired_count}\nExpiring: ${result.expiring_count}\nEmail: ${result.email_sent ? '✅' : '❌'}\nTelegram: ${result.telegram_sent ? '✅' : '❌'}`);
      } else {
        alert('Failed to send alerts');
      }
    } catch (error) {
      alert('Error sending alerts');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading alerts...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-100">Expired</p>
                <p className="text-3xl font-bold">{alertsData?.expired_count || 0}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-100">Expiring in 3 Days</p>
                <p className="text-3xl font-bold">{alertsData?.expiring_count || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Last Check</p>
                <p className="text-lg font-semibold text-gray-900">
                  {alertsData?.checked_at 
                    ? new Date(alertsData.checked_at).toLocaleString()
                    : 'Never'}
                </p>
              </div>
              <Button onClick={fetchAlerts} size="sm" variant="outline">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Send Alerts Button */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Send Expiry Alerts</h3>
              <p className="text-sm text-blue-700">Send alert summary to your email and Telegram</p>
            </div>
            <Button 
              onClick={sendAlerts} 
              disabled={sending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Send Alerts Now
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Expired Subscriptions */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-red-600 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Expired Subscriptions ({alertsData?.expired_count || 0})
          </CardTitle>
          <CardDescription>Subscriptions that have already expired</CardDescription>
        </CardHeader>
        <CardContent>
          {alertsData?.expired?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-red-50">
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Name</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Phone</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Email</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Package</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Expired On</th>
                  </tr>
                </thead>
                <tbody>
                  {alertsData.expired.slice(0, 50).map((sub, index) => (
                    <tr key={sub.id || index} className="border-b border-gray-100 hover:bg-red-50/50">
                      <td className="py-2 px-3 text-gray-900 font-medium">{sub.user_name || 'N/A'}</td>
                      <td className="py-2 px-3 text-gray-600">{sub.user_phone || 'N/A'}</td>
                      <td className="py-2 px-3 text-gray-600 text-xs">{sub.user_email || 'N/A'}</td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="border-red-300 text-red-700">
                          {sub.subscription_type}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-red-600">
                        {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
              No expired subscriptions ✅
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expiring Soon */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-yellow-600 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Expiring in 3 Days ({alertsData?.expiring_count || 0})
          </CardTitle>
          <CardDescription>Subscriptions expiring within the next 3 days</CardDescription>
        </CardHeader>
        <CardContent>
          {alertsData?.expiring_soon?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-yellow-50">
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Name</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Phone</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Email</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Package</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Days Left</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Expiry Date</th>
                  </tr>
                </thead>
                <tbody>
                  {alertsData.expiring_soon.slice(0, 50).map((sub, index) => (
                    <tr key={sub.id || index} className="border-b border-gray-100 hover:bg-yellow-50/50">
                      <td className="py-2 px-3 text-gray-900 font-medium">{sub.user_name || 'N/A'}</td>
                      <td className="py-2 px-3 text-gray-600">{sub.user_phone || 'N/A'}</td>
                      <td className="py-2 px-3 text-gray-600 text-xs">{sub.user_email || 'N/A'}</td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="border-yellow-300 text-yellow-700">
                          {sub.subscription_type}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">
                        <Badge className={sub.days_left <= 1 ? 'bg-red-500' : 'bg-yellow-500'}>
                          {sub.days_left} day{sub.days_left !== 1 ? 's' : ''}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-yellow-700">
                        {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
              No subscriptions expiring soon ✅
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Mentorship Link Manager has been moved to /admin/MentorshipLinkManager.jsx
// and is now rendered inside SettingsTab.

export default AdminDashboard;